import { Component, EventEmitter, Input, Output, OnInit, OnChanges } from '@angular/core';
import { IAuthenticationMethod } from 'app/security/core/typings/IAuthentication';
import { MultipleAuthenticator } from 'app/security/security-authentication/core/authentication-contracts';
import { AuthenticationTypes, AuthenticationProperties } from 'app/security/core/enums/authentication-types.enum';
import { I18nService } from 'i18n/i18n.service';
import { ShowToastService } from 'app/shared/services/showToast.service';

@Component({
  selector: 'multiple-auth-detail',
  templateUrl: './multiple-auth-detail.component.html',
  styleUrls: ['./multiple-auth-detail.component.scss']
})
export class MultipleAuthDetailComponent implements OnInit, OnChanges {
  editModeDomain: any = null;
  domainList: any[] = [];
  showInputForNewDomain: boolean = true;
  isLoadingData: boolean = false; // Flag to track if data is being loaded
  newDomainValue: string = '';
  newDomainDisplayName: string = '';
  selectedMethod: IAuthenticationMethod;
  selectedAuthenticator: MultipleAuthenticator;
  
  @Input() metadata: any;
  @Input() selectedMultipleAuthenticator: any;
  @Input() addNewMultipleAuthenticator: boolean = false;
  @Input() isActiveMaintenance: boolean = false;
  @Input() enableNewTabAuthentication: any;

  @Output() cancelMultipleAuthenticator = new EventEmitter();
  @Output() createMultipleAuthenticator = new EventEmitter();

  // ==================== GETTERS ====================
  get subtypes(): any[] {
    if (this.selectedAuthenticator?.type && this.metadata?.[this.selectedAuthenticator.type]?.[AuthenticationProperties.CONFIG]?.[AuthenticationProperties.SUBTYPES]?.[AuthenticationProperties.OPTIONS]) {
      return this.metadata[this.selectedAuthenticator.type][AuthenticationProperties.CONFIG][AuthenticationProperties.SUBTYPES][AuthenticationProperties.OPTIONS];
    }
    return [];
  }

  get flows(): any[] {
    if (this.selectedAuthenticator?.type === AuthenticationTypes.OAUTH2 && 
        this.selectedAuthenticator?.subtype && 
        this.metadata?.[this.selectedAuthenticator.type]?.[AuthenticationProperties.CONFIG]?.[AuthenticationProperties.FLOWS]?.[AuthenticationProperties.OPTIONS]?.[this.selectedAuthenticator.subtype]?.[AuthenticationProperties.OPTIONS]) {
      return this.metadata[this.selectedAuthenticator.type][AuthenticationProperties.CONFIG][AuthenticationProperties.FLOWS][AuthenticationProperties.OPTIONS][this.selectedAuthenticator.subtype][AuthenticationProperties.OPTIONS];
    }
    return [];
  }

  get activeFlow(): any {
    if (this.selectedAuthenticator?.oauthOptions?.[AuthenticationProperties.ACTIVE_FLOW]) {
      return this.selectedAuthenticator.oauthOptions[AuthenticationProperties.ACTIVE_FLOW];
    }
    return '';
  }

  get isDisabled(): boolean {
    return false;
  }

  set activeFlow(value: any) {
    if (this.selectedAuthenticator) {
      if (!this.selectedAuthenticator.oauthOptions) {
        this.selectedAuthenticator.oauthOptions = {};
      }
      this.selectedAuthenticator.oauthOptions[AuthenticationProperties.ACTIVE_FLOW] = value;
    }
  }
  // ==================== END GETTERS ====================

  constructor(private _i18nService: I18nService, private _showToastService: ShowToastService) { 
    this.initializeAuthenticator();
  }

  ngOnInit(): void {
    if (!this.selectedAuthenticator) {
      this.initializeAuthenticator();
    }
  }

  ngOnChanges(): void {
    this.isLoadingData = true;
    
    if (this.addNewMultipleAuthenticator) {
      this.initializeAuthenticator();
    }
    if (this.selectedMultipleAuthenticator) {
      this.selectedAuthenticator = JSON.parse(JSON.stringify(this.selectedMultipleAuthenticator));
      this.domainList = this.selectedAuthenticator?.domains ? [...this.selectedAuthenticator.domains] : [];
      this.showInputForNewDomain = this.domainList.length > 0 ? false : true;
    }
    
    setTimeout(() => {
      this.isLoadingData = false;
    }, 0);
  }

  onCancelNewAuthenticator() {
    this.cancelMultipleAuthenticator.emit();
  }

  onCreateNewAuthenticator() {
    if (!this.isFormValid()) {
      return;
    }

    // Prepare the complete authenticator object with all data
    const authenticatorToSave: MultipleAuthenticator = {
      displayName: this.selectedAuthenticator.displayName.trim(),
      type: this.selectedAuthenticator.type,
      subtype: this.selectedAuthenticator.subtype || '',
      enabled: this.selectedAuthenticator.enabled,
      domains: [...this.domainList], // Copy the domains from our table
    };

    if (this.selectedAuthenticator.type === AuthenticationTypes.OAUTH2) {
      authenticatorToSave.oauthOptions = { ...this.selectedAuthenticator.oauthOptions };
      authenticatorToSave.oauthOptions[AuthenticationProperties.ACTIVE_FLOW] = this.activeFlow;
    } else {
      authenticatorToSave.options = { ...this.selectedAuthenticator.options };
    }

    // Emit the complete object to parent
    this.createMultipleAuthenticator.emit(authenticatorToSave);
  }

  onSelectedTypeChange(type: string) {
    if (this.selectedAuthenticator) {
      this.selectedAuthenticator.type = type;
      this.selectedMultipleAuthenticator.type = type;
      // Only reset subtype and flow if this is a manual change, not during data loading
      if (!this.isLoadingData) {
        this.selectedAuthenticator.subtype = '';
        this.selectedMultipleAuthenticator.subtype = '';
        if (this.selectedAuthenticator.oauthOptions) {
          this.selectedAuthenticator.oauthOptions[AuthenticationProperties.ACTIVE_FLOW] = '';
          this.selectedMultipleAuthenticator.oauthOptions[AuthenticationProperties.ACTIVE_FLOW] = '';
        }
      }
    }
  }

  onSelectedSubtypeChange(subtype: string) {
    if (this.selectedAuthenticator) {
      this.selectedAuthenticator.subtype = subtype;
      this.selectedMultipleAuthenticator.subtype = subtype;
      // Only reset flow if this is a manual change, not during data loading
      if (!this.isLoadingData && this.selectedAuthenticator.oauthOptions) {
        this.selectedAuthenticator.oauthOptions[AuthenticationProperties.ACTIVE_FLOW] = '';
        this.selectedMultipleAuthenticator.oauthOptions[AuthenticationProperties.ACTIVE_FLOW] = '';
      }
    }
  }

  onSelectedFlowChange(flow: string) {
    this.activeFlow = flow;
  }

  onAddDomain() {
    this.showInputForNewDomain = true;
  }

  onCheckDomainBeforeAdd() {
    // Check if domain already exists
    if (this.domainList.some(domain => domain.name === this.newDomainValue)) {
      this._showToastService.showToast(this._i18nService.instant('AUTHENTICATION.ERROR_DOMAINSREPEATEDNAME'), 'error');
      return;
    }

    if (this.domainList.some(domain => domain.displayName === this.newDomainDisplayName)) {
      this._showToastService.showToast(this._i18nService.instant('AUTHENTICATION.ERROR_DOMAINSREPEATEDDISPLAYNAME'), 'error');
      return;
    }

    // Add new domain to the list
    this.domainList.push({
      name: this.newDomainValue.trim(),
      displayName: this.newDomainDisplayName.trim()
    });

    // Clear inputs and hide form
    this.onCancelDomain();
  }

  onCancelDomain() {
    this.showInputForNewDomain = false;
    this.newDomainValue = '';
    this.newDomainDisplayName = '';
  }

  onDomainKeydown(event: KeyboardEvent) {
    // Prevent space key
    if (event.key === ' ' || event.keyCode === 32) {
      event.preventDefault();
    }
  }

  cancelNewAuthenticator(): void {
    this.onCancelNewAuthenticator();
  }

  checkNecessaryFieldsForDomain() {
    return !this.newDomainValue.trim() || !this.newDomainDisplayName.trim();
  }

  deleteDomain(domain: any) {
    const index = this.domainList.indexOf(domain);
    if (index > -1) {
      this.domainList.splice(index, 1);
    }
  }

  isFormValid(): boolean {
    if (!this.selectedAuthenticator) {
      return false;
    }
    
    // Check required basic fields
    if (!this.selectedAuthenticator.displayName || 
        this.selectedAuthenticator.displayName.trim() === '' ||
        !this.selectedAuthenticator.type) {
      return false;
    }
    
    // Check if subtype is required and filled
    if (this.subtypes.length > 0 && (!this.selectedAuthenticator.subtype || this.selectedAuthenticator.subtype.trim() === '')) {
      return false;
    }
    
    // Check if flow is required and filled (only for OAuth2 with subtypes)
    if (this.flows.length > 0 && (!this.activeFlow || this.activeFlow.trim() === '')) {
      return false;
    }
    
    // Check if at least one domain is added
    if (this.domainList.length === 0) {
      return false;
    }
    
    // Check if user is not currently editing a domain
    if (this.editModeDomain !== null) {
      return false;
    }
    
    // Check if user is not currently adding a new domain
    if (this.showInputForNewDomain) {
      return false;
    }
    
    return true;
  }

  private initializeAuthenticator(): void {
    // Initialize the authenticator with default values
    this.selectedAuthenticator = {
      displayName: '',
      type: '',
      subtype: '',
      enabled: true,
      domains: [],
      options: {},
      oauthOptions: {}
    };
  }

}
